import React, { useState, useContext, useEffect } from "react";
import { Flex, Image, useThemeUI } from "theme-ui";
import {
  HeaderWrapper,
  HeaderActions,
  TitleWrapper,
  SubHeaderWrapper,
  TitleInner,
  ProfileWrapper,
  DropDown,
  DropDownToggle,
  LinkWrapper,
  SubHeaderInner,
  UserName,
  RoleName,
  NameWrapper,
  HeaderButtons,
  HeaderButton,
  ToggleButton,
  ModalTitle,
  ModalIcon,
  ModalSubtitle,
  ModalTitleWrapper,
} from "./styled";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import UsersIcon from "components/svg/icons/UsersIcon";
import UserContext from "context/User";
import SettingsIcon from "components/svg/icons/SettingsIcon";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import H1 from "components/typography/H1";
import { ProfileIcon, LogoutIcon, CheckMarkIcon } from "components/svg";
import { Form, Formik } from "formik";
import TextInput from "components/forms/TextInput";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { updateProfile } from "api/Users";
import LogoUpload from "./LogoUpload";
import AvatarSquareIcon from "components/svg/icons/AvatarSquareIcon";
import InstantEstimateTool from "../InstantEstimate";
import { trackActivity } from "api/ActivityTracker";
import GlobalSearch from "../GlobalSearch";
import SearchIcon from "components/svg/icons/SearchIcon";
import { resetLoginModal } from "helpers/loginModal";

const Header = ({ children, title }: any) => {
  const navigate = useNavigate();
  const toast = useToast();

  const themeContext = useThemeUI();
  const { theme } = themeContext;
  const { user, setUser }: any = useContext(UserContext);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleNav = () => {
    const customEvent = new Event("navBarToggle");
    document.body.dispatchEvent(customEvent);
  };

  const _logout = () => {
    void trackActivity({
      module: "auth",
      action: "logout",
      actionType: "click",
      userId: user?.id ?? null,
      companyId: user?.company?.id ?? null,
      metadata: {
        email: user?.email ?? null,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    localStorage.removeItem("user");
    setUser(null);
    resetLoginModal();
    navigate("/login");
  };

  const UserSchema = Yup.object()
    .shape({
      email: Yup.string().email("Invalid email").required("Email is required."),
      firstName: Yup.string().required("First Name is required."),
      lastName: Yup.string().required("Last Name is required."),
    })
    .noUnknown(true);

  const _onSubmit = async (values: any, actions: any) => {
    const update = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      data: {
        ...(!values?.data?.logo && user?.data?.logo ? { logo: "" } : {}),
      },
    };

    const res = await updateProfile({ update });
    if (res?.id) {
      toast.add("User updated.", "var(--theme-ui-colors-success)");
      setUser(res);
      setShowModal(false);
    } else {
      toast.add(res?.message ? res.message : "Error updating user", "var(--theme-ui-colors-danger)");
    }
  };

  return (
    <HeaderWrapper>
      <TitleWrapper>
        <TitleInner>
          <ToggleButton type="button" onClick={toggleNav} />
          <H1 fontSize={"22px"} color={theme?.colors?.base_800}>
            {title}
          </H1>
          {user && user.accessLevel !== "isProvider" && <InstantEstimateTool isCompany={user && user?.role} />}
        </TitleInner>
        <HeaderActions>
          <HeaderButtons>
            <HeaderButton onClick={() => setShowSearch(true)}>
              <SearchIcon width="19px" height="19px" fill="#000" />
            </HeaderButton>
            <HeaderButton onClick={() => {
              if (user?.accessLevel === "isClient" || user?.accessLevel === "isClientAdmin" || user?.accessLevel === "isCompany") {
                navigate("/settings");
              } else {
                setShowModal(true);
              }
            }}>
              <SettingsIcon width="19px" height="19px" fill="#000" />
            </HeaderButton>
            <HeaderButton onClick={_logout}>
              <LogoutIcon width="19px" height="19px" fill="#000" />
            </HeaderButton>
          </HeaderButtons>
          {user && (
            <ProfileWrapper>
              <NameWrapper>
                <UserName>
                  {user.firstName} {user.lastName}
                </UserName>
                <RoleName>
                  {user?.role?.title !== undefined && user?.role?.title !== null
                    ? user?.role?.title
                    : user?.accessLevel === "isProvider"
                    ? "Provider"
                    : user?.accessLevel === "isClientAdmin"
                    ? "Client Admin"
                    : "Client"}
                </RoleName>
              </NameWrapper>
              {user.data.logo ? <Image width="40px" height="40px" src={user.data.logo} style={{ borderRadius: 50 }} /> : <ProfileIcon width="40px" height="40px" fill="#BFBFBF" />}
            </ProfileWrapper>
          )}
        </HeaderActions>
      </TitleWrapper>
      {children && (
        <SubHeaderWrapper>
          <SubHeaderInner>{children}</SubHeaderInner>
        </SubHeaderWrapper>
      )}
      {showModal && (
        <Modal isOpen={showModal} toggle={() => setShowModal(!showModal)} centered={true} size="sm">
          <Formik initialValues={{ ...user }} onSubmit={_onSubmit} validationSchema={UserSchema} enableReinitialize>
            {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => setShowModal(!showModal)}>
                  <ModalIcon>
                    <UsersIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Profile settings</ModalTitle>
                    <ModalSubtitle>{"Edit a user's details."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <TextInput
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName", val)}
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
                  />
                  <TextInput
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName", val)}
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
                  />
                  {/* <TextInput
                    type="email"
                    name="email"
                    label="Email"
                    value={values.email}
                    onChange={(val: any) => setFieldValue("email", val)}
                    $errors={errors.email && submitCount > 0 ? errors.email : null}
                  /> */}
                  <Flex>
                    <label style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#818181", marginBottom: 8 }}>Profile Photo</label>
                  </Flex>
                  <Flex sx={{ gap: 20, alignItems: "center" }} style={{ marginBottom: 30 }}>
                    <Flex sx={{ flexDirection: "column" }}>
                      {values?.data?.logo && <Image src={values?.data?.logo} width={60} style={{ borderRadius: 6, marginRight: 10 }} />}
                      {!values?.data?.logo && AvatarSquareIcon({ width: 60, height: 60, fill: "#BFBFBF" })}
                    </Flex>
                    <LogoUpload user={user} title={values?.data?.logo ? "Replace Photo" : "Upload Photo"} afterUpload={(e) => setUser(e)} />
                    {values?.data?.logo && (
                      <BasicButton scheme="dangerSecondary" onClick={() => setFieldValue("data.logo", "")}>
                        Remove
                      </BasicButton>
                    )}
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  <BasicButton outline={true} styles={{ margin: ".25rem 10px .25rem 0" }} type="button" onClick={() => setShowModal(!showModal)} scheme="secondary">
                    Cancel
                  </BasicButton>
                  <BasicButton styles={{ margin: ".25rem 0 .25rem 0" }} type="submit" $submitting={isSubmitting} $iconHeight="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                    Update user
                  </BasicButton>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
      <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
    </HeaderWrapper>
  );
};

export default Header;
